import { useCallback, useEffect, useRef, useState } from 'react'
import { contractGameApi, selectContractItems, selectRandomContractItems } from '../services/contractGameApi'
import useUpdateBlocker from '../../../hooks/useUpdateBlocker'
import { beginUpdateOperation, isUpdateOperationBlocked, prepareUpdateRecovery } from '../../../services/appUpdateService'

const ANIMATION_MS = 4800

const useContractGame = (reducedMotion) => {
  const [inventory, setInventory] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [roundItems, setRoundItems] = useState([])
  const [phase, setPhase] = useState('loading')
  const [result, setResult] = useState(null)
  const [round, setRound] = useState(0)
  const versionRef = useRef(0)
  const busyRef = useRef(false)
  const timerRef = useRef(null)
  const mountedRef = useRef(false)
  useUpdateBlocker(() => busyRef.current || ['request', 'playing', 'requestError'].includes(phase), 'contract')

  const refresh = useCallback(async (preserveSelection = false) => {
    if (busyRef.current) return
    const version = ++versionRef.current
    const recoverUpdate = prepareUpdateRecovery('contract')
    setPhase('loading')
    setResult(null)
    setRoundItems([])
    try {
      const items = await contractGameApi.getInventory()
      if (!mountedRef.current || version !== versionRef.current) return
      recoverUpdate()
      setInventory(items)
      setSelectedIds((current) => preserveSelection
        ? current.filter((id) => items.some((item) => item.id === id))
        : selectContractItems(items).map((item) => item.id))
      setPhase('ready')
    } catch (error) {
      if (!mountedRef.current || version !== versionRef.current) return
      setInventory([])
      setSelectedIds([])
      setPhase('loadError')
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    refresh()
    return () => {
      mountedRef.current = false
      versionRef.current += 1
      window.clearTimeout(timerRef.current)
    }
  }, [refresh])

  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && (phase === 'ready' || phase === 'loadError')) refresh(phase === 'ready')
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [phase, refresh])

  const selected = selectedIds.map((id) => inventory.find((item) => item.id === id)).filter(Boolean)
  const available = inventory.filter((item) => !selectedIds.includes(item.id))

  const toggleItem = (id) => {
    if (phase !== 'ready') return
    setSelectedIds((current) => current.includes(id)
      ? current.filter((selectedId) => selectedId !== id)
      : current.length < 9 && inventory.some((item) => item.id === id) ? [...current, id] : current)
  }

  const randomize = () => {
    if (phase !== 'ready' || inventory.length < 3) return false
    const nextIds = selectRandomContractItems(inventory).map((item) => item.id)
    if (nextIds.length === selectedIds.length && nextIds.every((id, index) => id === selectedIds[index])) return false
    setSelectedIds(nextIds)
    return true
  }

  const clearSelection = () => {
    if (phase !== 'ready') return
    setSelectedIds([])
  }

  const sign = async () => {
    if (busyRef.current || isUpdateOperationBlocked('contract') || phase !== 'ready' || selected.length < 3) return
    busyRef.current = true
    const finishUpdateOperation = beginUpdateOperation('contract')
    ++versionRef.current
    setRoundItems(selected)
    setResult(null)
    setPhase('request')
    try {
      const signed = await contractGameApi.sign(selected)
      finishUpdateOperation()
      if (!mountedRef.current) return
      setResult(signed)
      setRound((current) => current + 1)
      setPhase(reducedMotion ? 'result' : 'playing')
      if (!reducedMotion) timerRef.current = window.setTimeout(() => {
        if (mountedRef.current) setPhase('result')
      }, ANIMATION_MS)
    } catch (error) {
      finishUpdateOperation(false)
      if (mountedRef.current) setPhase('requestError')
    } finally {
      busyRef.current = false
    }
  }

  return { inventory, selected, available, roundItems, phase, result, round, refresh, toggleItem, randomize, clearSelection, sign }
}

export default useContractGame
